import { loadScript } from '../utils/loadScript';

const AD_SCRIPT_TIMEOUT = 5000;
const AD_RETRY_DELAY_MS = 10000;

export interface AdInitResult {
  taddy: boolean;
  adexium: boolean;
  monetag: boolean;
  adsgram: boolean;
  telegramads: boolean;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

async function loadScriptWithRetry(
  label: string,
  url: string,
  options: Parameters<typeof loadScript>[1],
  onSuccess: () => void,
  result: AdInitResult,
  key: keyof AdInitResult,
): Promise<void> {
  // Бесконечные попытки загрузить SDK, пока не получится
  // Между попытками выдерживаем паузу AD_RETRY_DELAY_MS
  // Таймаут отдельной попытки задаётся через AD_SCRIPT_TIMEOUT
  // (options.timeout)
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await loadScript(url, options);
    if (res.success) {
      result[key] = true;
      try {
        onSuccess();
      } catch (e) {
        console.error(`[Ads] ${label} init error:`, (e as Error).message);
      }
      console.log(`[Ads] ${label} SDK loaded`);
      return;
    }

    console.warn(`[Ads] ${label} SDK failed to load, retrying in ${AD_RETRY_DELAY_MS}ms:`, res.error?.message);
    await delay(AD_RETRY_DELAY_MS);
  }
}

export async function initAds(): Promise<AdInitResult> {
  const result: AdInitResult = {
    taddy: false,
    adexium: false,
    monetag: false,
    adsgram: false,
    telegramads: false,
  };

  await Promise.all([
    loadScriptWithRetry(
      'Taddy',
      'https://sdk.taddy.pro/web/taddy.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
        attrs: { 'data-pub-id': 'd6ee8535c767c0fcd98fec3da11815c5' },
      },
      () => initTaddy(),
      result,
      'taddy',
    ),

    loadScriptWithRetry(
      'Adexium',
      'https://cdn.tgads.space/assets/js/adexium-widget.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => initAdexium(),
      result,
      'adexium',
    ),

    loadScriptWithRetry(
      'Monetag',
      'https://munqu.com/sdk.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
        attrs: { 'data-zone': '10280884', 'data-sdk': 'show_10641264' },
      },
      () => {
        // Для Monetag отдельной инициализации нет
      },
      result,
      'monetag',
    ),

    loadScriptWithRetry(
      'Adsgram',
      'https://sad.adsgram.ai/js/sad.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => {
        try {
          const Adsgram = (window as any).Adsgram;
          if (Adsgram && typeof Adsgram.init === 'function') {
            // Отдельный interstitial-блок для фонового шедулера
            const controller = Adsgram.init({
              blockId: 'int-23499',
              debug: false,
              showErrors: false
            });
            (window as any).adsgramInterstitialController = controller;
          }
        } catch (e) {
          console.error('[Adsgram] interstitial init error', e);
        }
      },
      result,
      'adsgram',
    ),

    loadScriptWithRetry(
      'TelegramAds',
      'https://richinfo.co/richpartners/telegram/js/tg-ob.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => initTelegramAds(),
      result,
      'telegramads',
    ),
  ]);

  console.log('[Ads] All SDK load attempts completed (at least once per network):', result);
  return result;
}

function initTaddy(): void {
  try {
    const Taddy = (window as any).Taddy;
    if (Taddy && typeof Taddy.ready === 'function') {
      Taddy.ready();
      console.log('[Taddy] ready() sent');
    }
  } catch (e) {
    console.error('[Taddy] ready error', e);
  }
}

function initAdexium(): void {
  try {
    const AdexiumWidget = (window as any).AdexiumWidget;
    if (!AdexiumWidget) return;

    const adexiumPush = new AdexiumWidget({
      wid: 'abe5228d-5fa3-48d1-851f-931dc91a5d88',
      adFormat: 'push-like',
      debug: false,
      firstAdImpressionIntervalInSeconds: 15,
      adImpressionIntervalInSeconds: 90,
      isFullScreen: false,
    });
    adexiumPush.autoMode();

    const adexiumInterstitial = new AdexiumWidget({
      wid: 'c8e3b41e-400e-40b1-bf10-5a06b2890f41',
      adFormat: 'interstitial',
      debug: false,
      isFullScreen: true,
    });
    (window as any).adexiumInterstitialInstance = adexiumInterstitial;

    console.log('[Adexium] widgets initialized');
  } catch (e) {
    console.error('[Adexium] init error', e);
  }
}

function initTelegramAds(): void {
  try {
    const TelegramAdsController = (window as any).TelegramAdsController;

    console.log('[TelegramAds] Checking SDK availability...');
    console.log('[TelegramAds] TelegramAdsController class:', TelegramAdsController);
    console.log('[TelegramAds] Type:', typeof TelegramAdsController);

    if (!TelegramAdsController) {
      console.error('[TelegramAds] SDK class not found in window');
      return;
    }

    // Создаем экземпляр контроллера
    console.log('[TelegramAds] Creating controller instance...');
    const controller = new TelegramAdsController();
    console.log('[TelegramAds] Controller instance created:', controller);

    if (!controller) {
      console.error('[TelegramAds] Controller instance is null');
      return;
    }

    console.log('[TelegramAds] Controller methods:', Object.keys(controller));
    console.log('[TelegramAds] triggerNativeNotification exists:', typeof controller.triggerNativeNotification);
    console.log('[TelegramAds] triggerInterstitialBanner exists:', typeof controller.triggerInterstitialBanner);

    console.log('[TelegramAds] Calling initialize with pubId: 979651, appId: 6561');
    controller.initialize({
      pubId: "979651",
      appId: "6561",
      debug: false
    });

    console.log('[TelegramAds] Initialize called, debug mode enabled');

    // Сохраняем экземпляр для использования
    (window as any).telegramAdsInstance = controller;

    console.log('[TelegramAds] Initialization complete, instance saved to window.telegramAdsInstance');
    console.log('[TelegramAds] triggerNativeNotification available:', typeof controller.triggerNativeNotification);
  } catch (e) {
    console.error('[TelegramAds] init error', e);
  }
}

export function startAdsScheduler(): void {
  const MIN_INTERVAL_MS = 5 * 60 * 1000;

  let lastFullscreenAdTs = 0;
  let adsgramInterstitialDisabledUntil = 0; // timestamp до которого Adsgram interstitial не трогаем
  let fullscreenAdToggle = 0; // для чередования: Adexium сначала или Monetag+Adsgram сначала

  setInterval(async () => {
    const now = Date.now();
    if (now - lastFullscreenAdTs < MIN_INTERVAL_MS) return;

    // Сразу обновляем таймер чтобы избежать спама
    lastFullscreenAdTs = now;

    const useAdexiumFirst = fullscreenAdToggle % 2 === 0;
    fullscreenAdToggle++;

    const tryShowAdexium = async (): Promise<boolean> => {
      const instance = (window as any).adexiumInterstitialInstance;
      if (!instance?.show) return false;
      try {
        await instance.show();
        lastFullscreenAdTs = Date.now();
        return true;
      } catch {
        return false;
      }
    };

    // Вариант полноэкранной рекламы: Monetag → TelegramAds (Native + Interstitial) → Adsgram
    const tryShowMonetag = async (): Promise<boolean> => {
      const showMonetag = (window as any).show_10641264;
      if (typeof showMonetag !== 'function') return false;
      try {
        const ymid = localStorage.getItem('telegram_id') || undefined;

        // Telegram Ads controller
        const telegramController = (window as any).telegramAdsInstance;
        const canUseTelegramAds = telegramController && typeof telegramController.triggerNativeNotification === 'function';

        // Подготавливаем Adsgram interstitial контроллер (int-23499), если не на кулдауне
        let adsgramController: any = null;
        const nowTs = Date.now();
        const canUseAdsgram = nowTs >= adsgramInterstitialDisabledUntil;
        if (canUseAdsgram) {
          try {
            const Adsgram = (window as any).Adsgram;
            adsgramController = (window as any).adsgramInterstitialController;

            if (!adsgramController && Adsgram && typeof Adsgram.init === 'function') {
              adsgramController = Adsgram.init({
                blockId: 'int-23499',
                debug: false,
                showErrors: false
              });
              (window as any).adsgramInterstitialController = adsgramController;
            }
          } catch (e) {
            console.error('[Adsgram interstitial] init error in scheduler', e);
          }
        }

        const monetagPromise = (async () => {
          await showMonetag({ type: 'preload', ymid });
          await showMonetag({ ymid });
        })();

        // Telegram Ads: Native + Interstitial с задержкой 1 сек между ними
        const telegramNativePromise = canUseTelegramAds
          ? (async () => {
              try {
                await telegramController.triggerNativeNotification(false);
              } catch (e) {
                console.error('[TelegramAds Scheduler] triggerNativeNotification error:', e);
              }
            })()
          : Promise.resolve();

        const telegramInterstitialPromise = canUseTelegramAds && typeof telegramController.triggerInterstitialBanner === 'function'
          ? (async () => {
              await delay(1000); // задержка 1 сек после Native
              try {
                await telegramController.triggerInterstitialBanner(false);
              } catch (e) {
                console.error('[TelegramAds Scheduler] triggerInterstitialBanner error:', e);
              }
            })()
          : Promise.resolve();

        // Adsgram запускаем с небольшой паузой (примерно через 1 секунду), если не на кулдауне.
        const adsgramPromise =
          canUseAdsgram && adsgramController && typeof adsgramController.show === 'function'
            ? (async () => {
                await delay(1000);
                try {
                  await adsgramController.show();
                } catch (result: any) {
                  // Adsgram no fill - это нормально, не логируем
                } finally {
                  // После любой попытки показа (успешной или неуспешной) ставим кулдаун на 10 секунд
                  adsgramInterstitialDisabledUntil = Date.now() + 10 * 1000;
                }
              })()
            : Promise.resolve();

        // Ждём завершения всех (Monetag + TelegramAds Native + TelegramAds Interstitial + Adsgram)
        await Promise.allSettled([
          monetagPromise,
          telegramNativePromise,
          telegramInterstitialPromise,
          adsgramPromise
        ]);

        // Обновляем таймер после любого показа
        lastFullscreenAdTs = Date.now();
        return true;
      } catch {
        return false;
      }
    };

    // Чередуем: один раз сначала Adexium, потом Monetag+Adsgram;
    // в следующий тик — сначала Monetag+Adsgram, потом Adexium.
    if (useAdexiumFirst) {
      if (!(await tryShowAdexium())) await tryShowMonetag();
    } else {
      if (!(await tryShowMonetag())) await tryShowAdexium();
    }
  }, 10000);
}
